// Lightweight history-based router for the back-office.
//
// The app renders pages from a single `active` string (see App.jsx
// renderPage). Detail pages receive a full entity object via `selectedX`
// state. Before this, every reload reset `active` to 'dashboard' — losing
// the user's place. This module syncs `active` (+ the selected entity id)
// to the URL so reload, back/forward, and deep links all work.
//
// Design:
//  - history.state holds a tiny {__nav, active, id} marker (survives reload).
//  - The full entity object is cached in sessionStorage keyed by active+id,
//    so reloading a detail page re-hydrates the exact object it had — no
//    per-page load-by-id needed.
//  - A pasted link in a fresh tab (no cache) degrades to the parent list
//    page rather than crashing a detail view with a partial object.


// active id -> { path, parent? }. Detail paths use :id. `parent` is the list
// surface a detail degrades to when its entity can't be hydrated.
export const ROUTE_TABLE = [
  { active: 'reports-mcp', path: '/reports-mcp' },
  // Rendered by the root shell itself, outside the sign-in gate (components/AcceptInvitationPage);
  // listed so the URL is a known route ("Accept invitation" in the tab, not "Not found").
  { active: 'accept-invitation', path: '/accept-invitation' },
  // top-level
  { active: 'dashboard', path: '/' },
  { active: 'events', path: '/events' },
  { active: 'organizers', path: '/organizers' },
  { active: 'event-requests', path: '/event-requests' },
  { active: 'orders', path: '/orders' },
  { active: 'people', path: '/people' },
  { active: 'accreditation', path: '/accreditation' },
  { active: 'users', path: '/users' },
  { active: 'finance', path: '/finance' },
  { active: 'treasury', path: '/treasury' },
  { active: 'transactions', path: '/transactions' },
  { active: 'split-payments', path: '/payment-plans' },
  { active: 'reconciliation', path: '/reconciliation' },
  { active: 'payouts', path: '/payouts' },
  { active: 'organizer-finances', path: '/organizer-finances' },
  { active: 'funding', path: '/funding' },
  { active: 'wallets', path: '/wallets' },
  { active: 'channels', path: '/channels' },
  { active: 'sales-setup', path: '/sales-setup' },
  { active: 'reports', path: '/reports' },
  { active: 'venues', path: '/venues' },
  { active: 'access-control', path: '/access-control' },
  { active: 'ticketing-inventory', path: '/ticketing-inventory' },
  { active: 'subscriptions', path: '/subscriptions' },
  { active: 'tournaments', path: '/tournaments' },
  { active: 'season-passes', path: '/season-passes' },
  { active: 'products', path: '/products' },
  { active: 'support', path: '/support' },
  { active: 'call-center', path: '/call-center' },
  { active: 'chat', path: '/chat' },
  { active: 'issues-approvals', path: '/issues-approvals' },
  { active: 'design', path: '/design' },
  { active: 'risk', path: '/risk' },
  { active: 'abuse', path: '/abuse' },
  // Ask lives on the Reports page (its button), so the sidebar keeps Reports lit while you are in it.
  { active: 'ask', path: '/ask', parent: 'reports' },
  { active: 'audit', path: '/audit' },
  { active: 'backend-capabilities', path: '/backend-map' },
  { active: 'functionality-reality', path: '/reality-map' },
  { active: 'release-notes', path: '/release-notes' },
  { active: 'settings', path: '/settings' },
  { active: 'utilities', path: '/utilities' },
  { active: 'team', path: '/team' },
  { active: 'health', path: '/health' },
  // detail (most specific first so matching prefers them)
  { active: 'box-office-detail', path: '/channels/box-office/:id', parent: 'channels' },
  { active: 'sales-channel-detail', path: '/channels/channel/:id', parent: 'channels' },
  { active: 'agent-detail', path: '/channels/agent/:id', parent: 'channels' },
  { active: 'team-detail', path: '/tournaments/team/:id', parent: 'tournaments' },
  { active: 'tournament-detail', path: '/tournaments/:id', parent: 'tournaments' },
  { active: 'event-detail', path: '/events/:id', parent: 'events' },
  { active: 'organizer-statement', path: '/organizers/:id/statement', parent: 'organizers' },
  { active: 'organizer-detail', path: '/organizers/:id', parent: 'organizers' },
  { active: 'request-review', path: '/event-requests/:id', parent: 'event-requests' },
  { active: 'order-detail', path: '/orders/:id', parent: 'orders' },
  // The id here is the ORDER uuid — an arrangement has no identity of its own, it IS an order
  // being collected in parts. Deliberately its own path rather than a tab on the order page: the
  // arrangement is what an operator is looking after, and it must be linkable on its own.
  { active: 'arrangement-detail', path: '/payment-plans/:id', parent: 'split-payments' },
  // One event's split setup. A distinct path from the core Events module's /events/:id — that page
  // carries the event record; this one carries only its split-payment relationship.
  { active: 'event-splits', path: '/payment-plans/event/:id', parent: 'split-payments' },
  { active: 'customer-detail', path: '/customers/:id', parent: 'people' },
  // A guest is a PERSON, so they get an address. Their invitation history, credential and reply
  // lived inside an edit dialog, which meant none of it could be linked to, shared with the
  // person chasing the RSVP, or reloaded — the whole record vanished on Escape.
  { active: 'guest-detail', path: '/accreditation/guest/:id', parent: 'accreditation' },
  { active: 'transaction-detail', path: '/transactions/:id', parent: 'transactions' },
  { active: 'payout-detail', path: '/payouts/:id', parent: 'payouts' },
  { active: 'event-settlement-detail', path: '/settlements/:id', parent: 'payouts' },
  { active: 'venue-detail', path: '/venues/:id', parent: 'venues' },
  // The Venue Studio: full screen, no sidebar or top bar (__root.tsx renders it bare).
  { active: 'venue-studio', path: '/venues/:id/studio', parent: 'venue-detail' },
  // The venue module (src/modules/venue-port): Quick Setup for a new venue or an existing one, and
  // Venue Canvas, full screen like the Studio.
  { active: 'venue-new', path: '/venues/new', parent: 'venues' },
  { active: 'venue-setup', path: '/venues/:id/setup', parent: 'venue-detail' },
  { active: 'venue-canvas', path: '/venues/:id/canvas', parent: 'venue-detail' },
  // Chat has no separate list/detail component (one page renders both the
  // conversation list and the open thread) — this just puts the SELECTED
  // conversation in the URL so it survives refresh AND can be shared/
  // bookmarked, the same as every other detail route already does. A plain
  // sessionStorage key (the first version of this) only solved "survives
  // refresh in this tab" — pasting the link to a colleague landed them on an
  // empty list, not the conversation being discussed.
  { active: 'chat-thread', path: '/chat/:id', parent: 'chat' },
  // A REPORT IS A THING PEOPLE SEND EACH OTHER. It had a deep link before this
  // (`/reports?report=RPT-146`) and that link was write-only in the wrong direction:
  // the param was CONSUMED on arrival (`setDeepReportId('')`), and every way of
  // opening a report — the library row, the Eye button, the runs tab, the exports
  // tab, a chart shortcut — set local state and wrote nothing to the URL. So a
  // producer that already knew an id (the MCP, an email) could hand out a link,
  // while an operator actually LOOKING at a report had nothing to copy: the address
  // bar said `/reports` either way. Same shape the chat-thread note above describes,
  // one level worse.
  //
  // A URL is an ADDRESS, not a message. As a route it survives reload and back/
  // forward, resolves cold through the REGISTRY in queries/entityById, and a dead id
  // gets DetailNotFound instead of a silent library listing.
  //
  // `/reports/run/:id` outranks `/reports/:id` on segment count (see `compile`), so a
  // run never shadows a report definition.
  { active: 'report-run', path: '/reports/run/:id', parent: 'reports' },
  { active: 'report-detail', path: '/reports/:id', parent: 'reports' },
];

const BY_ACTIVE = Object.fromEntries(ROUTE_TABLE.map((r) => [r.active, r]));

// Compile a /a/b/:id template into a matcher. More segments and fewer params
// rank higher so detail routes win over their list parents.
const compile = (route) => {
  const segs = route.path.split('/').filter(Boolean);
  const params = segs.filter((s) => s.startsWith(':')).length;
  const re = new RegExp('^/' + segs.map((s) => (s.startsWith(':') ? '([^/]+)' : s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('/') + '/?$');
  return { route, re, hasParam: params > 0, score: segs.length * 10 - params };
};
/**
 * FORMER PATHS, still read and never written. Payment Plans lived at /split-payments while it held
 * both splits and instalment plans; links to it are in bookmarks and chats, so the old paths open
 * the same pages, and `canonicalPath` moves the address bar to the current one. pathForRoute reads
 * BY_ACTIVE (the table above), so nothing links to these any more.
 */
const FORMER_PATHS = [
  { active: 'split-payments', path: '/split-payments' },
  { active: 'arrangement-detail', path: '/split-payments/:id' },
  { active: 'event-splits', path: '/split-payments/event/:id' },
];
const MATCHERS = [...ROUTE_TABLE, ...FORMER_PATHS].map(compile).sort((a, b) => b.score - a.score);

/** The current path for a former one, or the path unchanged. */
// /reconciliation was a mock statement-matching page (invented KPIs, invented reviewers); its address now opens Transactions,
// where a payment is found by its gateway reference.
// /wallets computed balances nothing holds (an organizer's "Available" was what had already been paid out to them, fees a
// flat 4.5%, refund float from a column that is NULL on cancelled orders); where ticket money sits is Treasury's.
export const canonicalPath = (pathname) => (pathname || '').replace(/^\/split-payments(?=\/|$)/, '/payment-plans').replace(/^\/reconciliation\/?$/, '/transactions').replace(/^\/wallets\/?$/, '/treasury');

// `routeId` lets an entity be addressed by a stable, backend-resolvable id (e.g.
// orders/transactions route by the order UUID, not the human reference on `.id`).
export const entityId = (entity) => (entity && (entity.routeId || entity.id || entity.code)) || '';

export const pathForRoute = (active, entity) => {
  const route = BY_ACTIVE[active];
  if (!route) {
    // The same silent-fallback disease as parsePathname, in the write direction: navigating to an
    // id that is not in the table sent the operator HOME, so a mistyped or renamed route id looked
    // like "the app went to the dashboard for no reason" instead of naming itself. Loud while
    // developing, where it is a bug that must be fixed; never a white screen for an operator.
    const message = `pathForRoute: no route registered for "${active}" — add it to ROUTE_TABLE. Navigating home would hide this.`;
    if (import.meta.env?.DEV) throw new Error(message);
    console.error(message);
    return '/';
  }
  if (route.path.includes(':id')) {
    const id = entityId(entity);
    return id ? route.path.replace(':id', encodeURIComponent(id)) : (BY_ACTIVE[route.parent]?.path || '/');
  }
  return route.path;
};

// The list route a detail route belongs to ('order-detail' → 'orders'), and a human name
// for the thing it addresses. Used by the not-found state so a dead deep link can say what
// it failed to find and offer the way back, instead of silently rendering the parent list.
export const parentOfRoute = (active) => BY_ACTIVE[active]?.parent || null;
export const labelForRoute = (active) =>
  (active || '').replace(/-detail$/, '').replace(/-/g, ' ') || 'record';

// pathname -> { active, id } (id is the raw URL segment, no cached object yet)
/**
 * A ROUTING MISS IS A MISS — it is not the dashboard.
 *
 * This used to `return { active: 'dashboard' }` for any path that matched nothing, and the write
 * direction (pathForRoute) mirrored it by returning '/'. So EVERY routing mistake — a typo'd id, a
 * stale link, a route removed from the table, a path a future refactor forgets — surfaced as
 * "you are on the home dashboard". That is the worst possible failure mode: it is indistinguishable
 * from a legitimate visit to home, so nobody can tell an error from normal use, and the bug reports
 * it produces ("switching pages flashes the dashboard") point at the symptom rather than the cause.
 *
 * The fallback was never load-bearing: `/` matches ROUTE_TABLE's own `{ active: 'dashboard' }`
 * entry, so a real visit home resolves through the table like every other page. The fallback only
 * ever caught errors — and then hid them.
 *
 * `active: null` means "no route owns this URL". Callers render a not-found, which is what the
 * console already does for a dead detail link (DetailNotFound); this just extends the same honesty
 * from "that record is gone" to "that page does not exist".
 */
export const parsePathname = (pathname) => {
  const clean = (pathname || '/').replace(/\/+$/, '') || '/';
  for (const m of MATCHERS) {
    const match = clean.match(m.re);
    if (match) return { active: m.route.active, id: m.hasParam ? decodeURIComponent(match[1]) : null };
  }
  return { active: null, id: null };
};

// --- entity cache (sessionStorage) so reload re-hydrates the full object ----
const cacheKey = (active, id) => `nav:${active}:${id}`;

// The cache is a STORE, and a store that changes silently can only be read by whoever
// happens to re-render. `cacheEntity` is the single write, so it is also the single
// notification — that is what lets the page title be derived rather than assigned.
const cacheSubscribers = new Set();
export const onEntityCached = (fn) => { cacheSubscribers.add(fn); return () => cacheSubscribers.delete(fn); };

export const cacheEntity = (active, entity) => {
  const id = entityId(entity);
  if (!id) return;
  try { window.sessionStorage.setItem(cacheKey(active, id), JSON.stringify(entity)); } catch { /* quota / serialization — non-fatal */ }
  cacheSubscribers.forEach((fn) => fn(active, id));
};
/**
 * An edit to an entity, merged into every route's cached copy of it — so the tab title and the
 * breadcrumb, which read this cache, say the new name the moment the edit lands. Without it the
 * query caches updated (lib/entities patchEntity) while this one kept the old row until a reload:
 * a venue renamed to "Fourteen Twenty" still titled its tab "Fourteen Twenty (Malfy acceptance)".
 */
export const patchCachedEntity = (id, next) => {
  if (!id || typeof window === 'undefined') return;
  try {
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (!key || !key.startsWith('nav:') || !key.endsWith(`:${id}`)) continue;
      const active = key.slice(4, key.length - String(id).length - 1);
      const old = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (!old) continue;
      window.sessionStorage.setItem(key, JSON.stringify({ ...old, ...next }));
      cacheSubscribers.forEach((fn) => fn(active, String(id)));
    }
  } catch { /* quota / serialization — non-fatal, as cacheEntity */ }
};
export const readCachedEntity = (active, id) => {
  if (!id) return null;
  try { const raw = window.sessionStorage.getItem(cacheKey(active, id)); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
// NOTE: TanStack Router now owns URL/history (see src/router.tsx + App.jsx).
// This module is reduced to pure helpers: ROUTE_TABLE, parsePathname,
// pathForRoute, and the sessionStorage entity cache (cacheEntity /
// readCachedEntity) used to rehydrate a detail object on same-session reload.
// The old useUrlRouting/getInitialNav/resolveNav history machinery was removed.
